//#region src/sharedConfig.ts
const validEnvironments = ["production", "development"];
/**
* Canonical production domains.
* - API:         https://api.skylight.health
* - Tenant apps: https://<tenant>.app.skylight.health
*/
const API_DOMAIN = "api.skylight.health";
const APP_BASE_DOMAIN = "app.skylight.health";
/** Registrable domain shared by the API and tenant apps. Session cookies are named per tenant and set on this domain so `api.skylight.health` still receives them. */
const ROOT_DOMAIN = "skylight.health";
/** Public status page (apps/status-page), linked from customer-facing error screens. */
const STATUS_PAGE_URL = "https://status.skylight.health";
const DEFAULT_WEB_APP_PORT = 5173;
const DEFAULT_SERVER_PORT = 3e3;
const PRODUCTION_SERVER_PORT = 8080;
/**
* Ports `.bin/dev` sets via WEB_APP_PORT / SERVER_PORT so the agent server
* can sit beside `pnpm dev` without stealing 5173/3000.
*/
const AGENT_WEB_APP_PORT = 5174;
const AGENT_SERVER_PORT = 3001;
/**
* Browser header carrying the tenant slug when Origin has none (localhost
* path-based routing). Production RPC uses Origin; this is the fallback.
*/
const TENANT_SLUG_HEADER = "x-tenant-slug";
/**
* Tenant slugs. These are user-visible: in production a slug is the app
* subdomain (`<slug>.app.skylight.health`), and outside production it is the
* first path segment (`/<slug>/task`).
*
* A new slug needs a DNS record for its subdomain before it is reachable in
* production; they are not wildcarded.
*/
let TenantSlug = /* @__PURE__ */ function(TenantSlug) {
	/** The customer-facing demo tenant, at demo.app.skylight.health. */
	TenantSlug["Demo"] = "demo";
	TenantSlug["HHB"] = "hhb";
	TenantSlug["Skylight"] = "skylight";
	return TenantSlug;
}({});
/**
* Narrow an untrusted string to a TenantSlug.
*
* Slugs arrive from outside the type system — `tenants.slug` is a nullable text column,
* and subdomains and environment variables are plain strings. Anything that turns one
* into a credential lookup has to check it first, because an unknown slug derives a
* secret name that does not exist.
*/
function isTenantSlug(value) {
	return value != null && Object.values(TenantSlug).includes(value);
}
/**
* Real phone calls and Spruce SMS are allowed for every tenant in production,
* and only for the Demo tenant in development.
*/
function allowsLiveSideEffects(tenantSlug, environmentString = process.env.NODE_ENV) {
	const { environment } = getSharedConfig(environmentString);
	if (environment === "production") return true;
	return tenantSlug === TenantSlug.Demo;
}
function parsePort(raw, fallback) {
	if (raw == null || raw === "") return fallback;
	const port = Number(raw);
	if (!Number.isInteger(port) || port <= 0 || port > 65535) throw new Error(`Invalid port "${raw}"`);
	return port;
}
function validateEnvironment(environmentString) {
	if (environmentString === "test") return "development";
	if (validEnvironments.includes(environmentString)) return environmentString;
	throw new Error(`Invalid environment: ${environmentString}. Must be one of: ${validEnvironments.join(", ")}`);
}
function getSharedConfig(environmentString) {
	if (!environmentString) throw new Error("process.env.NODE_ENV is required");
	const environment = validateEnvironment(environmentString);
	const webAppPort = parsePort(process.env.WEB_APP_PORT, DEFAULT_WEB_APP_PORT);
	const serverPort = parsePort(process.env.SERVER_PORT, environment === "production" ? PRODUCTION_SERVER_PORT : DEFAULT_SERVER_PORT);
	return {
		environment,
		gcpProjectId: "saffron-health",
		applicationsServiceAccount: "core-services-sa@saffron-health.iam.gserviceaccount.com",
		dbAdminServiceAccount: "backend-sa@saffron-health.iam.gserviceaccount.com",
		productAnalyticsServiceAccount: "product-analytics-sa@saffron-health.iam.gserviceaccount.com",
		productAnalyticsBucket: "saffron-product-analytics",
		vertexAiLocation: "global",
		langfuseBaseUrl: "https://langfuse.skylight.health",
		webAppPort,
		webAppBaseDomain: environment === "production" ? APP_BASE_DOMAIN : `localhost:${webAppPort}`,
		cookieDomain: environment === "production" ? `.${ROOT_DOMAIN}` : ".localhost",
		serverPort,
		serverUrl: environment === "production" ? `https://${API_DOMAIN}` : `https://localhost:${serverPort}`,
		demoUserCredentials: {
			name: "Demo User",
			email: "support@skylight.health",
			password: "paqvy6-fuhGok-xebsuq"
		},
		skylightUserCredentials: {
			name: "Skylight Medical Group User",
			email: "skylight@test.dev",
			password: "skylight-password-123"
		}
	};
}

//#endregion
export { AGENT_SERVER_PORT, AGENT_WEB_APP_PORT, API_DOMAIN, APP_BASE_DOMAIN, DEFAULT_SERVER_PORT, DEFAULT_WEB_APP_PORT, PRODUCTION_SERVER_PORT, ROOT_DOMAIN, STATUS_PAGE_URL, TENANT_SLUG_HEADER, TenantSlug, allowsLiveSideEffects, getSharedConfig, isTenantSlug };